import React, { useState, useEffect } from "react";
// Assuming these UI components exist in the components/ui directory
import Input from "../components/ui/Input";
// import SelectBox from "../components/ui/SelectBox"; // <-- Replaced with SearchableSelectBox
import Button from "../components/ui/Button";
import InputWithPrepend from "../components/ui/InputWithPrepend";
import SearchableCountryCodeDropdown from "../components/ui/SearchableCountryCodeDropdown";

// Using the new searchable component for improved UX
import SearchableSelectBox from "../components/ui/SearchableSelectBox";

// REQUIRED NEW IMPORT
import { API_BASE_PATH, fetchCountries, fetchStatesForCountry, fetchStoresWhereStaffAssigned, fetchStoresWithStaff } from "../apiHelper";
import { useNavigate, useLocation } from "react-router-dom";
import FastAPIClient from "../services/apiClient";

const client = new FastAPIClient();

/**
 * EditCustomerDetails Component
 * Provides a form structure for capturing essential details for a new customer.
 */
const EditCustomerDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const customerId = location.state?.customerId;

    const [customerObject, setCustomerObject] = useState({
        // ... (existing fields)
        // 1. Customer Type (Business/Individual)
        customer_type: 'C', // Default to Business ('B')

        // 2. Primary Contact
        person_title: '', // Title: Mr, Mrs, Miss, Ms
        first_name: '',
        middle_name: '',
        last_name: '',

        // NEW: Company Name field
        company_name: '',

        // 3. Email Address
        email_address: '',

        // 4. Phone Number components
        country_code: '+234', // Default country code
        phone_number: '',

        bvn: '',

        // ADDRESS FIELDS
        address1: '',
        address2: '',
        zipcode: '',
        city: '',
        state: '', // Stores the state/province NAME
        country: '', // Stores the country NAME
        country_abbr: '', // Helper to store abbreviation for API use

        // NEW: Store ID for customer creation
        store_id: null, // Will hold the selected store_id
    });
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [toast, showToast] = useState({ message: '', show: false, toastType: 1 });
    const [countries, setCountries] = useState([]); // List of countries for dropdown
    const [states, setStates] = useState([]); // List of states for dropdown
    const [isFetchingStates, setIsFetchingStates] = useState(false); // Loading state for states

    // NEW: State for stores
    const [stores, setStores] = useState([]); // List of stores for dropdown
    const [isFetchingStores, setIsFetchingStores] = useState(false); // Loading state for stores

    // ----------------------------------------------------------------------
    // --- Effects ---
    // ----------------------------------------------------------------------
    useEffect(() => {
        const lcUser = localStorage.getItem("user")
        if (!lcUser) {
            showToast({ message: "Please login to add new customers.", show: true, toastType: 0 });
            navigate('/login');
        } else {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);
            // console.log(parsedUser)
            // After setting the user, we can fetch stores
            loadStores(parsedUser);
        }
    }, [showToast, navigate])

    // NEW: Function to load stores and set default store_id
    const loadStores = async (currentuser) => {
        setIsFetchingStores(true);
        try {
            // console.log(user)
            const fetchedStores = await fetchStoresWhereStaffAssigned(currentuser);

            // Map the fetched stores to options for SearchableSelectBox
            const storeOptions = fetchedStores.map((s) => ({
                label: s.identifier,
                value: s.store_id, // Store the store_id in value
                staff: s.staff,
            }));
            setStores(storeOptions);
            const defaultStoreId = storeOptions.length > 0 ? storeOptions[0].value : null;

            // Set the store_id in the customer object state
            if (defaultStoreId !== null) {
                setCustomerObject(prev => ({
                    ...prev,
                    store_id: defaultStoreId,
                }));
            }

        } catch (err) {
            console.error("Error loading stores:", err);
            showToast({ message: "Failed to load stores for selection.", show: true, toastType: 0 });
        } finally {
            setIsFetchingStores(false);
        }
    };


    useEffect(() => {
        // Fetch countries list on component mount
        const loadCountries = async () => {
            try {
                const fetchedCountries = await fetchCountries();
                const countryOptions = fetchedCountries.map((c) => ({
                    label: c.name,
                    value: c.name, // Store name in value for the SelectBox
                    abbr: c.country_abbr,
                }));
                setCountries(countryOptions);
            } catch (err) {
                console.error("Error loading countries:", err);
                showToast({ message: "Failed to load countries.", show: true, toastType: 0 });
            }
        };
        loadCountries();
    }, []);

    // --- Fetching existing customer details ---
    // ----------------------------------------------------------------------
    useEffect(() => {
        // If customerId is missing (e.g., direct URL access without state), handle gracefully
        if (!customerId) {
            showToast({
                message: "No customer selected for editing.",
                show: true,
                toastType: 0
            });
            setTimeout(() => navigate('/sales/customers'), 2000); // Redirect back to list
            return;
        }

        const fetchCustomerData = async () => {
            setIsLoading(true);
            try {
                // Call GET API endpoint created on the backend
                const response = await client.fetchData(`${API_BASE_PATH}/users/customer-details/${customerId}`);
                // console.log(response);
                // Populate component state with fetched customer data
                setCustomerObject({
                    customer_type: response.customer_type || 'C',
                    person_title: response.person_title || '',
                    first_name: response.first_name || '',
                    middle_name: response.middle_name || '',
                    last_name: response.last_name || '',
                    company_name: response.company_name || '',
                    email_address: response.email_address || '',
                    country_code: response.country_code || '+234',
                    phone_number: response.phone_number || '',
                    bvn: response.bvn || '',
                    address1: response.address1 || '',
                    address2: response.address2 || '',
                    zipcode: response.zipcode || '',
                    city: response.city || '',
                    state: response.state || '',
                    country: response.country || '',
                    country_abbr: response.country_abbr || '',
                    store_id: response.store_id || null,
                });

                // If state options need to be preloaded for the existing country:
                if (response.country_abbr) {
                    const fetchedStates = await fetchStatesForCountry(response.country_abbr);
                    setStates(fetchedStates.map(s => ({ label: s.name, value: s.name })));
                }

            } catch (error) {
                console.error("Error fetching customer details:", error);
                const errorMsg = error.response?.data?.detail || "Failed to load customer details.";
                showToast({ message: errorMsg, show: true, toastType: 0 });
            } finally {
                setIsLoading(false);
            }
        };

        fetchCustomerData();
    }, [customerId, navigate]);

    // ----------------------------------------------------------------------
    // --- Handlers ---
    // ----------------------------------------------------------------------

    // Prevents form submission when Enter key is pressed on an input field
    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    // Handler for generic text and phone code inputs and store_id
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCustomerObject((prevObject) => ({
            ...prevObject,
            [name]: value,
        }));
    };

    // Handler for radio button changes
    const handleRadioChange = (e) => {
        setCustomerObject((prevObject) => ({
            ...prevObject,
            customer_type: e.target.value,
        }));
    };

    // Handler for Country selection which triggers state fetching
    const handleCountryChange = async (e) => {
        const selectedCountryName = e.target.value;
        const selectedCountry = countries.find(c => c.value === selectedCountryName);
        const abbr = selectedCountry ? selectedCountry.abbr : '';

        // 1. Update the country name, abbreviation, and reset state field
        setCustomerObject(prev => ({
            ...prev,
            country: selectedCountryName,
            country_abbr: abbr,
            state: '', // IMPORTANT: Reset state when country changes
        }));

        // 2. Fetch states if an abbreviation is found
        setStates([]); // Clear existing states immediately
        if (abbr) {
            setIsFetchingStates(true);
            try {
                const fetchedStates = await fetchStatesForCountry(abbr);
                const stateOptions = fetchedStates.map((s) => ({
                    label: s.name,
                    value: s.name, // Store name in value for the SelectBox
                }));
                setStates(stateOptions);
            } catch (err) {
                console.error(`Error loading states for ${abbr}:`, err);
                showToast({ message: `Failed to load states for ${selectedCountryName}.`, show: true, toastType: 0 });
            } finally {
                setIsFetchingStates(false);
            }
        }
    };


    // Form submission handler (Placeholder - add your API logic here)
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!customerObject.store_id) {
            showToast({ message: "Please select a Store/Branch.", show: true, toastType: 0 });
            return;
        }

        const cleanPhone = customerObject.phone_number.replace(/[\s\-\(\)]/g, '');
        const fullPhoneNumber = customerObject.country_code + cleanPhone;

        const payload = {
            ...customerObject,
            phone_number: cleanPhone,
            full_phone_number: fullPhoneNumber,
            store_id: Number(customerObject.store_id),
        };

        try {
            console.log(payload)
            const response = await client.postData(
                `${API_BASE_PATH}/users/edit-customer-details/${customerId}`,
                payload
            );
            console.log(response);
            showToast({ message: "Customer updated successfully!", show: true, toastType: 1 });
        } catch (error) {
            const errorMessage = error.response?.data?.detail || "Failed to update customer.";
            showToast({ message: errorMessage, show: true, toastType: 0 });
        }
    };

    // Options for Primary Contact Title (person_title)
    const titleOptions = [
        { label: "Mr", value: "Mr" },
        { label: "Mrs", value: "Mrs" },
        { label: "Miss", value: "Miss" },
        { label: "Ms", value: "Ms" },
    ];

    // Country Code Dropdown Component for prepending
    const CountryCodePrepend = (
        <SearchableCountryCodeDropdown
            name="country_code"
            value={customerObject.country_code}
            onChange={handleInputChange}
        // Removed onMouseDown here. We apply it to InputWithPrepend below.
        />
    );

    // Options for store selection from the fetched stores state
    const storeOptions = stores.map(store => ({
        label: store.label,
        value: store.value.toString(), // Convert number to string for SelectBox value consistency
    }));


    return (
        <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 gap-8">
                <div className="bg-white rounded-lg shadow-md p-6 max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                    <div className="mb-6">
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">Edit Customer Details</h1>
                        <p className="text-gray-600 text-md">Edit the essential information for your business customer.</p>
                    </div>

                    {/* Toast Notification */}
                    {toast.show && (
                        <div className={`p-3 mb-4 rounded-md text-sm font-medium ${toast.toastType === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                            {toast.message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>

                        {/* NEW: Store Selection */}
                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">
                                Customer's Store / Branch
                            </h2>
                            <SearchableSelectBox
                                name="store_id"
                                // The value needs to be a string for SearchableSelectBox consistency
                                value={customerObject.store_id ? customerObject.store_id.toString() : ''}
                                onChange={handleInputChange}
                                label="Store / Branch"
                                required={true} // Customer must be assigned to a store
                                options={storeOptions}
                                placeholder={isFetchingStores ? "Loading stores..." : "Select Store or Branch"}
                                isDisabled={isFetchingStores || stores.length === 0}
                                containerClassName="mb-0"
                            />
                        </div>
                        {/* END NEW: Store Selection */}

                        {/* 1. Customer Type (Radio Buttons) */}
                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">
                                Customer Type
                            </h2>
                            <div className="flex items-center space-x-6">
                                {/* Business Radio */}
                                <label htmlFor="type-business" className="flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        id="type-business"
                                        name="customer_type"
                                        value="B"
                                        checked={customerObject.customer_type === 'B'}
                                        onChange={handleRadioChange}
                                        className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                    />
                                    <span className="ml-2 text-sm font-medium text-gray-700">Business</span>
                                </label>

                                {/* Individual Radio */}
                                <label htmlFor="type-individual" className="flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        id="type-individual"
                                        name="customer_type"
                                        value="C"
                                        checked={customerObject.customer_type === 'C'}
                                        onChange={handleRadioChange}
                                        className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                    />
                                    <span className="ml-2 text-sm font-medium text-gray-700">Individual</span>
                                </label>
                            </div>
                        </div>

                        {/* 2. Primary Contact Information (Title, First Name, Last Name, Company Name) */}
                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Primary Contact</h2>

                            {/* Title, First Name, Middle Name, Last Name Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                                {/* Title */}
                                <div className="md:col-span-2">
                                    <SearchableSelectBox
                                        name="person_title"
                                        value={customerObject.person_title}
                                        onChange={handleInputChange}
                                        label="Title"
                                        required={false}
                                        options={titleOptions}
                                        placeholder="Title"
                                        containerClassName="mb-0"
                                    />
                                </div>

                                {/* First Name */}
                                <div className="md:col-span-4">
                                    <Input
                                        name="first_name"
                                        value={customerObject.first_name}
                                        onChange={handleInputChange}
                                        onKeyDown={handleInputKeyDown}
                                        label="First Name"
                                        type="text"
                                        id="first_name"
                                        placeholder="First name"
                                        required={true}
                                    />
                                </div>

                                {/* Middle Name (Optional) */}
                                <div className="md:col-span-3">
                                    <Input
                                        name="middle_name"
                                        value={customerObject.middle_name}
                                        onChange={handleInputChange}
                                        onKeyDown={handleInputKeyDown}
                                        label="Middle Name"
                                        type="text"
                                        id="middle_name"
                                        placeholder="Middle name (Optional)"
                                        required={false}
                                    />
                                </div>

                                {/* Last Name */}
                                <div className="md:col-span-3">
                                    <Input
                                        name="last_name"
                                        value={customerObject.last_name}
                                        onChange={handleInputChange}
                                        onKeyDown={handleInputKeyDown}
                                        label="Last Name"
                                        type="text"
                                        id="last_name"
                                        placeholder="Last name"
                                        required={true}
                                    />
                                </div>
                            </div>

                            {/* NEW: Company Name Field (Full width) */}
                            <div className="mt-4">
                                <Input
                                    name="company_name"
                                    value={customerObject.company_name}
                                    onChange={handleInputChange}
                                    onKeyDown={handleInputKeyDown}
                                    label="Company Name"
                                    type="text"
                                    id="company_name"
                                    placeholder="e.g., Acme Corporation (Required for Business Customers)"
                                    required={customerObject.customer_type === 'B'}
                                />
                            </div>
                        </div>

                        {/* 3. & 4. Email and Phone Number */}
                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Contact Information</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {/* Email Address */}
                                <Input
                                    name="email_address"
                                    value={customerObject.email_address}
                                    onChange={handleInputChange}
                                    onKeyDown={handleInputKeyDown}
                                    label="Email Address"
                                    type="email"
                                    id="email_address"
                                    placeholder="name@example.com"
                                    required={false}
                                />

                                {/* Phone Number (InputWithPrepend and SearchableCountryCodeDropdown) */}
                                <div className="ml-1">
                                    <InputWithPrepend
                                        label="Phone Number"
                                        id="phone_number"
                                        name="phone_number"
                                        type="tel"
                                        placeholder="e.g., 555 123 4567"
                                        value={customerObject.phone_number}
                                        onChange={handleInputChange}
                                        onKeyDown={handleInputKeyDown}
                                        required={true}
                                        prepend={CountryCodePrepend}
                                        className="pl-2"
                                    />
                                </div>

                                {/* NEW: BVN Input Field */}
                                <div className="mt-4">
                                    <Input
                                        name="bvn"
                                        value={customerObject.bvn}
                                        onChange={handleInputChange}
                                        onKeyDown={handleInputKeyDown}
                                        label="BVN (Bank Verification Number)"
                                        type="text"
                                        id="bvn"
                                        placeholder="Enter 11-digit BVN (Optional)"
                                        maxLength={11}
                                        required={false}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* NEW SECTION: Billing Address */}
                        <div className="mb-8 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Billing Address</h2>

                            {/* Country/Region */}
                            <div className="mb-4">
                                <SearchableSelectBox
                                    name="country"
                                    value={customerObject.country}
                                    onChange={handleCountryChange} // Use dedicated handler
                                    label="Country/Region"
                                    required={false}
                                    options={countries}
                                    placeholder="Select a country"
                                    isDisabled={countries.length === 0}
                                    containerClassName="mb-0"
                                />
                            </div>

                            {/* Address Line 1 & 2 */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <Input name="address1" value={customerObject.address1} onChange={handleInputChange} onKeyDown={handleInputKeyDown} label="Address Line 1" type="text" id="address1" placeholder="Street address, P.O. box" />
                                <Input name="address2" value={customerObject.address2} onChange={handleInputChange} onKeyDown={handleInputKeyDown} label="Address Line 2" type="text" id="address2" placeholder="Apartment, suite, unit, building (Optional)" />
                            </div>

                            {/* State/Province, City, Zip Code */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                {/* State/Province */}
                                <SearchableSelectBox
                                    name="state"
                                    value={customerObject.state}
                                    onChange={handleInputChange}
                                    label="State / Province"
                                    required={false}
                                    options={states}
                                    placeholder={isFetchingStates ? "Loading states..." : "Select state/province"}
                                    isDisabled={!customerObject.country || isFetchingStates}
                                    containerClassName="mb-0"
                                />

                                {/* City */}
                                <Input name="city" value={customerObject.city} onChange={handleInputChange} onKeyDown={handleInputKeyDown} label="City" type="text" id="city" placeholder="City" />

                                {/* Zip Code */}
                                <Input name="zipcode" value={customerObject.zipcode} onChange={handleInputChange} onKeyDown={handleInputKeyDown} label="Zip / Postal Code" type="text" id="zipcode" placeholder="Zip or Postal Code" />
                            </div>
                        </div>
                        {/* END NEW SECTION */}


                        {/* Submit Button */}
                        <div className="flex justify-end pt-4">
                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                className="flex items-center justify-center"
                                // Disable button if stores are loading or if no store is selected
                                disabled={isFetchingStores || !customerObject.store_id}
                            >
                                <svg className="w-5 h-5 text-white mr-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14M5 12h14" />
                                </svg>
                                Create Customer
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditCustomerDetails;